import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { NgRedux } from '@angular-redux/store';
import { TranslateService } from '@ngx-translate/core';

import {
    AccountTicketsFacade,
    AccountTicketsNgrxFacade,
    CustomerFacade,
    MediumService,
    RelatedCardService,
    ShoppingCartActions,
    ShoppingCartService,
    TicketsService
} from '@ridango/components';
import {
    AppStartupService,
    AuthService,
    LogService,
    Notification,
    NotificationService,
    ShoppingCart,
    Ticket
} from '@ridango/core';
import { NOTIFICATION_ROLES } from '@ridango/core/lib/enums/notifications.enum';

import { BehaviorSubject } from 'rxjs';

import { GotlandActions } from '../../redux/gotland.actions';
import { PurchaseProcessService } from './purchase-process.service';
import { FirebaseService } from '../../services/firebase.service';
import Env from '@ridango/core/env';
import { IonContent } from '@ionic/angular';
import { Temporal } from '@js-temporal/polyfill';

@Injectable({
    providedIn: 'root'
})
export class HelperService {
    // https://angularfirebase.com/lessons/sharing-data-between-angular-components-four-methods/
    private messageSource = new BehaviorSubject<Notification>(null);
    currentMessage = this.messageSource.asObservable();

    private notificationQueue: Notification[] = [];
    ionContent: IonContent;

    constructor(
        private ngRedux: NgRedux<any>,
        public appConfig: AppStartupService,
        private ticketService: TicketsService,
        private cartService: ShoppingCartService,
        private ls: LogService,
        private actions: ShoppingCartActions,
        private notifications: NotificationService,
        private router: Router,
        public ridangoAuth: AuthService,
        private purchaseProgressService: PurchaseProcessService,
        private relatedCardsService: RelatedCardService,
        public translate: TranslateService,
        private mediumService: MediumService,
        private gotlandActions: GotlandActions,
        private accountTicketsFacade: AccountTicketsFacade,
        private accountTicketsFacadeNgrx: AccountTicketsNgrxFacade,
        private firebaseService: FirebaseService,
        private customerFacade: CustomerFacade,
        @Inject('ENV') public env: Env
    ) {}
    postLocalTicket(ticket: Ticket, cart: ShoppingCart = null) {
        this.cartService.addLocalTicketToCart(ticket, ticket.medium_object, cart).subscribe(
            (t: Ticket) => {
                // TODO: look at the comment
                // cleanup, as we do not know from which view request came from, then both purchase flow and wallet flow needs to be cleaned
                this.purchaseProgressService.setPurchaseProgress(0);
                this.purchaseProgressService.resetAreas();
                // 1. create new 'local' ticket with addition of doc num and price from server ticket
                const localTicket: Ticket = JSON.parse(JSON.stringify(ticket));
                localTicket.doc_num = t.doc_num;
                localTicket.price = t.price;
                this.cartService.getCartContentsById(t.shopping_cart_id).subscribe(
                    (c: ShoppingCart) => {
                        this.ngRedux.dispatch(this.actions.fetchCartSuccess());
                        this.ls.log(this, 'get cart success', c);
                        c.tickets = JSON.parse(JSON.stringify(cart.tickets));
                        c.docNums = [...cart.docNums, localTicket.doc_num];

                        // 2. substitute cart ticket with new 'local' one, as it has necessary info for ticket component
                        if (cart.shopping_cart_id) {
                            c.tickets = { ...cart.tickets, [t.doc_num]: localTicket };
                        } else {
                            c.tickets = { [localTicket.doc_num]: localTicket };
                        }
                        this.ngRedux.dispatch(this.actions.updateShoppingCart(c));
                        this.ngRedux.dispatch(this.actions.addTicketToCart(localTicket));
                        window.scroll(0, 0);
                        this.addNotification(
                            new Notification(
                                this.notifications.notificationTypes.SUCCESS,
                                'LOCAL_TICKET_ADDED_TO_CART_SUCCESS'
                            )
                        );
                        this.router.navigate([this.translate.currentLang, 'purchase', 'cart']);
                    },
                    (err) => {
                        window.scroll(0, 0);
                        this.addNotification(new Notification(this.notifications.notificationTypes.ERROR, err.error));
                        throw new Error(err);
                    }
                );
            },
            (err) => {
                window.scroll(0, 0);
                this.addNotification(new Notification(this.notifications.notificationTypes.ERROR, err.error));
            },
            () => {
                this.ngRedux.dispatch(this.gotlandActions.cartPending(false));
            }
        );
    }

    batchPostTicket(
        ticket: Ticket,
        cart: ShoppingCart = null,
        cardIds = [],
        relatedCardIds = [],
        cardType = '',
        emails = [],
        language = ''
    ) {
        this.mediumService.setSelectedMedium(null);

        this.cartService
            .batchAddTicketsToCart(ticket, null, cart, 'web', cardIds, relatedCardIds, cardType, emails, language)
            .subscribe(
                (data: any) => {
                    this.ngRedux.dispatch(this.actions.addLocalTicketUidToCart(ticket));

                    this.ls.log(this, 'batchPostTicket 1', data);
                    if (data.tickets && data.tickets.length > 0) {
                        const pr = data.tickets.reduce((price, t) => price + t.price, 0);
                        ticket.price = pr;
                        data.tickets.map((t) => {
                            this.ngRedux.dispatch(this.actions.addLocalTicketToCart(ticket, t, cart));
                        });

                        if (data.errors && data.errors.length > 0) {
                            data.errors.map((e) => {
                                this.addNotificationToQueue(
                                    new Notification(this.notifications.notificationTypes.ERROR, e.error_message)
                                );
                            });
                        }
                        this.getCartData(data.tickets[0]);
                        // // naviagate to cart page
                    } else {
                        // no navigation, all tickets failed
                        if (data.errors && data.errors.length > 0) {
                            data.errors.map((e) => {
                                this.addNotification(
                                    new Notification(this.notifications.notificationTypes.ERROR, e.error_message)
                                );
                            });
                            window.scroll(0, 0);
                        }
                    }
                },
                (err) => {
                    this.ls.log(this, 'addLocalTicketToCart', err);
                }
            );
    }

    getCartData(t) {
        this.cartService.getCartContentsById(t.shopping_cart_id).subscribe(
            (c: ShoppingCart) => {
                this.ngRedux.dispatch(this.actions.fetchCartSuccess());
                this.ls.log(this, 'get cart success', c);

                let tickets = {};
                c.tickets.map((ct) => {
                    tickets = { ...tickets, [ct.doc_num]: ct };

                    if (ct.card_type === 'Q') {
                        this.ngRedux.dispatch(this.actions.addVoucherTicketToCart(ct));
                    }
                });
                c.tickets = tickets;

                this.ngRedux.dispatch(this.actions.updateCorpShoppingCart(c));

                window.scroll(0, 0);
                // this.addNotificationToQueue( new Notification( this.notifications.notificationTypes.SUCCESS, 'LOCAL_TICKET_ADDED_TO_CART_SUCCESS' ) );
                this.router.navigate([this.translate.currentLang, 'purchase', 'cart']);
            },
            (err) => {
                this.ngRedux.dispatch(this.actions.fetchCartError());
                this.addNotification(new Notification(this.notifications.notificationTypes.ERROR, err.error));
                window.scroll(0, 0);
                throw new Error(err);
            },
            () => {
                this.ngRedux.dispatch(this.gotlandActions.cartPending(false));
                this.ngRedux.dispatch(this.actions.fetchCartDone());
            }
        );
    }

    addNotification(notification: Notification) {
        this.notifications.add(notification);
    }

    addNotificationToQueue(notification: Notification) {
        notification.role = NOTIFICATION_ROLES.ALERT;
        this.notificationQueue.push(notification);
    }

    displayNotifications() {
        this.notifications.deleteAll();
        this.notificationQueue.forEach((noti) => {
            this.notifications.add(noti);
        });
        this.notificationQueue = [];
    }

    cleanupAfterLogout() {
        this.relatedCardsService.resetRelatedCards();
        this.relatedCardsService.setSelectedCard(null);
        this.mediumService.setSelectedMedium(null);
        this.cartService.deleteCart();
        this.accountTicketsFacade.resetAccountTickets();
        this.accountTicketsFacadeNgrx.resetAccountTickets();
        this.firebaseService.unsubscribe();
        this.customerFacade.clearCustomerProfile();

        if (this.env.NOTIFICATION_PAGE_USER) {
            this.firebaseService.checkNotificationSetting();
        }
    }

    cleanupAfterLogin() {
        this.relatedCardsService.setSelectedCard(null);
        this.mediumService.setSelectedMedium(null);
        this.cartService.deleteCart();
    }

    isAllowedPath(path) {
        return this.env.ALLOWED_PATHS.includes(path);
    }

    setIonContentRef(ionContent: IonContent) {
        this.ionContent = ionContent;
    }

    scrollToTop() {
        this.ionContent?.scrollToTop(300);
    }

    appDateToServerDateTime(appDate: string | Temporal.PlainDate): Temporal.Instant {
        const serverDateTime = Temporal.PlainDate.from(appDate)
            .toPlainDateTime({ hour: 0, minute: 0, second: 0 })
            .toZonedDateTime(this.appConfig.getTimezone())
            .toInstant();

        return serverDateTime;
    }

    appDateTimeToServerDateTime(appDateTime: string | Temporal.PlainDateTime): Temporal.Instant {
        if (!appDateTime) {
            return null;
        }

        let serverDateTime: Temporal.Instant;

        if (typeof appDateTime === 'string') {
            try {
                // If the string already represents an Instant (e.g. ends with 'Z' or has an offset),
                // parse it directly as Instant. This avoids errors like
                // "Z designator not supported for PlainDateTime".
                serverDateTime = Temporal.Instant.from(appDateTime);
            } catch {
                // Fallback: treat the string as a PlainDateTime in the app timezone.
                const plainDateTime = Temporal.PlainDateTime.from(appDateTime);
                serverDateTime = plainDateTime.toZonedDateTime(this.appConfig.getTimezone()).toInstant();
            }
        } else {
            // appDateTime is already a Temporal.PlainDateTime
            serverDateTime = appDateTime.toZonedDateTime(this.appConfig.getTimezone()).toInstant();
        }

        return serverDateTime;
    }

    serverDateTimeToAppDateTime(serverDate: string | Temporal.Instant): Temporal.ZonedDateTime {
        const appDateTime = Temporal.Instant.from(serverDate).toZonedDateTimeISO(this.appConfig.getTimezone());

        return appDateTime;
    }
}
